import React, { createContext, useCallback, useEffect, useReducer } from 'react'

import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool,
    CognitoUserSession,
} from 'amazon-cognito-identity-js'

import axios from '../utils/axios'
import { cognitoConfig } from '../config'
import { jwtDecode } from 'jwt-decode'

const INITIALIZE = 'INITIALIZE'
const SIGN_OUT = 'SIGN_OUT'

const UserPool = new CognitoUserPool({
    UserPoolId: cognitoConfig.userPoolId || '',
    ClientId: cognitoConfig.clientId || '',
    endpoint: cognitoConfig.endpoint,
})

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    isSuperadmin: false,
    user: null,
}

type CognitoStateUser = {
    displayName: string
    groups?: Array<'superadmin'>
}

export interface CognitoState {
    isAuthenticated: boolean
    isSuperadmin: boolean
    isInitialized: boolean
    user: CognitoStateUser | null
    method: 'cognito'
    signIn: (email: string, pwd: string) => Promise<{ newPasswordRequired: boolean }>
    signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>
    resetPassword: (email: string) => Promise<{ success: true } | { success: false; message: string }>

    signOut: () => void
}

const reducer = (state: CognitoState, action: { payload?: Partial<CognitoState>; type: string }) => {
    if (action.type === INITIALIZE) {
        const { isAuthenticated, user } = action.payload as Pick<CognitoState, 'isAuthenticated' | 'user'>
        return {
            ...state,
            isAuthenticated,
            isSuperadmin: user?.groups?.includes('superadmin') ?? false,
            isInitialized: true,
            user,
        }
    }
    if (action.type === SIGN_OUT) {
        return {
            ...state,
            isAuthenticated: false,
            user: null,
        }
    }
    return state
}

const AuthContext = createContext<CognitoState | null>(null)

function AuthProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(reducer, initialState)

    const getUserAttributes = useCallback(
        (currentUser: CognitoUser): Promise<CognitoState['user']> =>
            new Promise((resolve, reject) => {
                currentUser.getUserAttributes((err, attributes) => {
                    if (err) {
                        reject(err)
                    } else {
                        const results =
                            attributes?.reduce(
                                (acc, { Name, Value }) => ({ ...acc, [Name]: Value }),
                                {} as CognitoStateUser,
                            ) || null

                        resolve(results)
                    }
                })
            }),
        [],
    )

    const getSession = useCallback(
        () =>
            new Promise((resolve, reject) => {
                const user = UserPool.getCurrentUser()
                if (user) {
                    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
                        void (async () => {
                            if (err) {
                                reject(err)
                            } else {
                                try {
                                    const attributes = await getUserAttributes(user)

                                    if (attributes === null) {
                                        throw new Error('Failed to get user attributes for user')
                                    }

                                    const token = session?.getIdToken().getJwtToken()
                                    const decodedToken = jwtDecode<{ exp: number }>(token || '')
                                    const expiryTime = decodedToken.exp * 1000 // Convert to milliseconds

                                    localStorage.setItem('token_expiry', expiryTime.toString())
                                    attributes.groups = session?.getIdToken()?.payload['cognito:groups'] || []

                                    axios.defaults.headers.common.Authorization = `Bearer ${token}`

                                    dispatch({
                                        type: INITIALIZE,
                                        payload: {
                                            isAuthenticated: true,
                                            user: attributes,
                                        },
                                    })

                                    resolve({
                                        user,
                                        session,
                                        headers: { Authorization: token },
                                    })
                                } catch (err) {
                                    reject(err as Error)
                                }
                            }
                        })()
                    })
                } else {
                    dispatch({
                        type: INITIALIZE,
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    })
                }
            }),
        [getUserAttributes],
    )

    const initialize = useCallback(async () => {
        try {
            await getSession()
        } catch {
            dispatch({
                type: INITIALIZE,
                payload: {
                    isAuthenticated: false,
                    user: null,
                },
            })
        }
    }, [getSession])

    useEffect(() => {
        void initialize()
    }, [initialize])

    const signIn = useCallback(
        (email: string, password: string) =>
            new Promise((resolve, reject) => {
                const user = new CognitoUser({
                    Username: email,
                    Pool: UserPool,
                })

                const authDetails = new AuthenticationDetails({
                    Username: email,
                    Password: password,
                })

                user.authenticateUser(authDetails, {
                    onSuccess: async (data) => {
                        await getSession()
                        resolve(data)
                    },
                    onFailure: (err) => {
                        if (err.message.includes('is disabled')) {
                            err.message =
                                'Användaren har inaktiverats. Vänligen kontakta info@financelab.se för mer information.'
                        }
                        reject(err)
                    },
                    newPasswordRequired: () => {
                        resolve({ newPasswordRequired: true })
                    },
                })
            }),
        [getSession],
    )

    const signOut = () => {
        const user = UserPool.getCurrentUser()
        if (user) {
            user.signOut()
            dispatch({ type: SIGN_OUT })
        }
    }

    const signUp = (email: string, password: string, firstName: string, lastName: string) =>
        new Promise((resolve, reject) => {
            UserPool.signUp(
                email,
                password,
                [
                    new CognitoUserAttribute({ Name: 'email', Value: email }),
                    new CognitoUserAttribute({
                        Name: 'name',
                        Value: `${firstName} ${lastName}`,
                    }),
                ],
                [],
                async (err) => {
                    if (err) {
                        reject(err)
                        return
                    }
                    resolve(undefined)
                    // Set destination URL here
                    window.location.href = ''
                },
            )
        })

    const resetPassword = async (email: string) => {
        try {
            const { default: UserApi } = await import('../api/UserApi')
            const userApi = new UserApi()
            // Call the API to request a password reset code
            await userApi.requestPasswordResetCode(email)
            return { success: true }
        } catch (error) {
            // Return the error message to handle it in the component
            return { success: false, message: error.response?.data?.message || 'Failed to request password reset code' }
        }
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'cognito',
                user: {
                    displayName: state?.user?.name || 'Undefined',
                    role: 'user',
                    ...state.user,
                },
                signIn,
                signUp,
                signOut,
                resetPassword,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export { AuthContext, AuthProvider, UserPool }
